import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { unitsApi } from "../../services/api";
import Spinner from "../common/Spinner";
import ErrorAlert from "../common/ErrorAlert";
import { Unit, UnitStatus } from "@akfdev-com/shared/src/models";

interface BuildingUnitsProps {
  buildingId: string;
}

const BuildingUnits: React.FC<BuildingUnitsProps> = ({ buildingId }) => {
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState<Unit[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUnits = async () => {
      if (!buildingId) return;
      try {
        setLoading(true);
        const data = await unitsApi.getAll(buildingId);
        setUnits(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("Failed to load units. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, [buildingId]);

  if (loading) {
    return <Spinner message="Loading units..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900">
          Units
          {units.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({units.length})
            </span>
          )}
        </h3>
        <Link
          to={`/dashboard/units/new?building=${buildingId}`}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
        >
          Add Unit
        </Link>
      </div>

      {units.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p className="mt-2 text-sm font-medium text-gray-900">No units yet</p>
          <p className="mt-1 text-sm text-gray-500">
            Add your first unit to this building.
          </p>
          <div className="mt-4">
            <Link
              to={`/dashboard/units/new?building=${buildingId}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-blue-200 focus:outline-none"
            >
              Add Unit
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {units.map((unit) => (
              <li key={unit.id}>
                <Link
                  to={`/dashboard/units/${unit.id}`}
                  className="block hover:bg-gray-50 px-4 py-4 sm:px-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-medium text-primary-600">
                        {unit.name}
                      </p>
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          unit.status === UnitStatus.Occupied
                            ? "bg-green-100 text-green-800"
                            : unit.status === UnitStatus.Pending
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {unit.status.charAt(0).toUpperCase() +
                          unit.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {unit.floor && <span>{unit.floor} floor</span>}
                      <span>share: {unit.shareNumerator}/{unit.shareDenominator}</span>
                      <span className="text-gray-400">
                        {unit.residents.filter((r: any) => !r.moveOutDate)
                          .length > 0
                          ? `${
                              unit.residents.filter((r: any) => !r.moveOutDate)
                                .length
                            } resident${
                              unit.residents.filter((r: any) => !r.moveOutDate)
                                .length !== 1
                                ? "s"
                                : ""
                            }`
                          : "No residents"}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BuildingUnits;
